import { Component, Injector, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ApAlertService } from 'webcommon/core-ui';
import { FcModifyZipListItemDto, FcZipListItemDto } from 'webcommon/shared';
import { FcGenericItemModalBaseComponent } from '../../generic/fc-generic-item-modal-base.component';
import { FcListValidationService } from '../../generic/fc-list-validation/fc-list-validation.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  providers: [ApAlertService, FcListValidationService],
  selector: 'ap-fc-zip-list-modal',
  templateUrl: './fc-zip-list-modal.component.html',
})
export class FcZipListModalComponent extends FcGenericItemModalBaseComponent implements OnInit {
  public override baseId = 'fc-zip-item';
  public modalWarningText = 'A ZIP code must be either of the following #####, #####-####, A#A #A#, CP ##### or C.P. #####';

  // List Item with default value
  public listItem: FcZipListItemDto = {
    Id: '',
    ExternalID: '',
    Name: '',
    Notes: '',
    Inactive: false,
    UserDefined: true,
    State: '',
    City: '',
    AreaCode: '',
  };

  // Form Controls
  public externalIdControl: FormControl = new FormControl('');
  public zipControl: FormControl = new FormControl('');
  public cityControl: FormControl = new FormControl('');
  public stateControl: FormControl = new FormControl('');
  public areaCodeControl: FormControl = new FormControl('');
  public notesControl: FormControl = new FormControl('');
  public inactiveControl: FormControl = new FormControl(false);
  public userDefinedControl: FormControl = new FormControl(true);

  public showZipError = false;

  // Form Group
  public form = new FormGroup({
    externalId: this.externalIdControl,
    name: this.zipControl,
    city: this.cityControl,
    state: this.stateControl,
    areaCode: this.areaCodeControl,
    notes: this.notesControl,
    inactive: this.inactiveControl,
    userDefined: this.userDefinedControl,
  });

  public zipListValidation = {
    ExternalID: {
      MaxLength: 25,
    },
    Name: {
      IsRequired: true,
      MinLength: 5,
      MaxLength: 10,
    },
    Notes: {
      MaxLength: 65535,
    },
    City: {
      MaxLength: 25,
    },
    State: {
      MaxLength: 3,
    },
    AreaCode: {
      RegExp: '[0-9]{3}',
    },
  };

  constructor(injector: Injector) {
    super(injector);

    this.zipControl.valueChanges.pipe(distinctUntilChanged(), debounceTime(10)).subscribe(() => {
      this.showZipError = false;
      if (this.zipControl.errors) {
        setTimeout(() => {
          this.showZipError = true;
        }, 0);
      }
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();

    // Load List Item and set default validation rules
    this.loadListItem(this.zipListValidation);
  }

  public setForm(): void {
    // External Id Control
    this.externalIdControl.setValidators(this.fcListValidation.getValidators('ExternalID'));
    this.externalIdControl.setValue(this.listItem.ExternalId);

    // Zip Control
    this.zipControl.setValidators(this.fcListValidation.getValidators('Name'));
    const isZipControlDisabled = !this.listItem.UserDefined;
    if (isZipControlDisabled) {
      this.zipControl.disable();
    }
    this.zipControl.setValue(this.listItem.Name); // Should call 'Name' cause in db has 'Name' property

    // City Control
    this.cityControl.setValidators(this.fcListValidation.getValidators('City'));
    this.cityControl.setValue(this.listItem.City);

    // State Control
    this.stateControl.setValue(this.listItem.State);
    this.stateControl.setValidators(this.fcListValidation.getValidators('State'));

    // AreaCode Control
    this.areaCodeControl.setValidators(this.fcListValidation.getValidators('AreaCode'));
    this.areaCodeControl.setValue(this.listItem.AreaCode);

    // Notes Control
    this.notesControl.setValidators(this.fcListValidation.getValidators('Notes'));
    this.notesControl.setValue(this.listItem.Notes);

    // Inactive Control
    this.inactiveControl.setValue(this.listItem.Inactive);

    // User Defined Control
    this.userDefinedControl.setValue(this.listItem.UserDefined);
    this.userDefinedControl.disable();
  }

  public override save(): void {
    const values = this.form.getRawValue();
    const modifyItemDto: FcModifyZipListItemDto = {
      ExternalID: values.externalId,
      Name: values?.name?.toUpperCase(),
      Inactive: values.inactive,
      Notes: values.notes,
      UserDefined: values.userDefined,
      City: values.city,
      AreaCode: values.areaCode,
      State: values.state,
    };

    super.save<FcModifyZipListItemDto>(modifyItemDto);
  }
}
